import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import { showError } from '../api';
import { COMPETITIVENESS, deliveryText, money, TYPE_LABEL, UNKNOWN_COST } from '../format';
import { useLoad } from '../hooks';
import { partnerApi, PartnerMe, PartnerProgramView } from './api';

type Rec = Record<string, any>;
interface SessionDraft {
  key: string;
  id?: number;
  removed?: boolean;
  f: Rec;
}

const SESSION_KEYS = [
  'label', 'start_date', 'end_date', 'dates_text', 'delivery_mode', 'housing_type', 'hours_per_week', 'location_name', 'is_host_location',
  'city', 'state_name', 'zip', 'application_open_date', 'priority_deadline', 'deadline_date', 'decision_date', 'cost_min', 'cost_max', 'is_free', 'price_includes',
];
const PROGRAM_KEYS = [
  'display_name', 'program_type', 'when_offered', 'program_status', 'notes', 'website', 'grades_eligible', 'age_min', 'age_max', 'gpa_min',
  'citizenship_requirement', 'gender_restriction', 'residency_states', 'first_gen_or_income_based', 'prior_experience_note', 'essays_count',
  'essays_word_count', 'essays_topics', 'teacher_recommendations_count', 'general_recommendations_count', 'application_fee', 'fee_waiver_available',
  'transcript_required', 'test_scores', 'resume', 'interview_required', 'portfolio_required', 'audition_required', 'need_based_aid',
  'merit_scholarship', 'full_scholarship_available', 'pays_students', 'financial_aid_available',
];
const NUM = new Set(['hours_per_week', 'cost_min', 'cost_max', 'age_min', 'age_max', 'gpa_min', 'essays_count', 'essays_word_count', 'teacher_recommendations_count', 'general_recommendations_count']);
const DATE = new Set(['start_date', 'end_date', 'application_open_date', 'priority_deadline', 'deadline_date', 'decision_date']);

/** Normalize a value from the database for editing and comparing. */
const norm = (k: string, v: any): any => {
  if (v === undefined || v === '') return null;
  if (DATE.has(k)) return v ? String(v).slice(0, 10) : null;
  if (NUM.has(k)) return v == null ? null : Number(v);
  return v;
};
/** Turn the edited form value into what the API accepts. */
const out = (k: string, v: any): any => {
  if (v === '' || v === undefined) return null;
  if (NUM.has(k)) return v == null ? null : Number(v);
  return v;
};
const same = (a: any, b: any) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
let seq = 0;
const newKey = () => `n${++seq}`;

const SECTIONS: [string, string][] = [
  ['basics', 'Basics'],
  ['sessions', 'Sessions'],
  ['who', 'Who can apply'],
  ['needs', 'What students need'],
  ['money', 'Money'],
  ['topics', 'Topics'],
  ['competitive', 'How competitive'],
];

/** Edit one program (or add a new one with id "new"). */
export default function ProgramEditor({ id, me, onSaved }: { id: string; me: PartnerMe; onSaved: () => void }) {
  const history = useHistory();
  const isNew = id === 'new';
  const pid = isNew ? 0 : Number(id);
  const data = useLoad<PartnerProgramView | null>(() => (isNew ? Promise.resolve(null) : partnerApi.program(pid)), [pid]);
  const topicGroups = useLoad(() => partnerApi.topics(), []);

  const [orig, setOrig] = useState<Rec>({});
  const [p, setP] = useState<Rec>({});
  const [name, setName] = useState('');
  const [sessions, setSessions] = useState<SessionDraft[]>([]);
  const [origSessions, setOrigSessions] = useState<Map<number, Rec>>(new Map());
  const [topics, setTopics] = useState<string[]>([]);
  const [origTopics, setOrigTopics] = useState<string[]>([]);
  const [comp, setComp] = useState<{ label: string | null; rate: string; source: string }>({ label: null, rate: '', source: '' });
  const [origComp, setOrigComp] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ field: string; message: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [topicAsk, setTopicAsk] = useState('');

  useEffect(() => {
    if (isNew) {
      setSessions([{ key: newKey(), f: { delivery_mode: 'in_person', is_host_location: true } }]);
      return;
    }
    if (!data.data) return;
    const o: Rec = {};
    PROGRAM_KEYS.forEach((k) => (o[k] = norm(k, data.data!.program[k])));
    setOrig(o);
    setP(o);
    const om = new Map<number, Rec>();
    setSessions(
      data.data.sessions.map((s) => {
        const f: Rec = {};
        SESSION_KEYS.forEach((k) => (f[k] = norm(k, s[k])));
        om.set(Number(s.id), f);
        return { key: `s${s.id}`, id: Number(s.id), f: { ...f } };
      }),
    );
    setOrigSessions(om);
    const t = data.data.topics.map((x) => x.slug);
    setTopics(t);
    setOrigTopics(t);
    const label = data.data.program.competitiveness_set_by === 'partner' ? data.data.program.competitiveness_label : null;
    setComp({ label, rate: '', source: '' });
    setOrigComp(label);
  }, [data.data, isNew]);

  const needs = me.needs_you.find((n) => n.program_id === pid);
  const errFor = (field: string) => errors.find((e) => e.field === field)?.message;
  const set = (k: string, v: any) => setP({ ...p, [k]: v });
  const setS = (key: string, k: string, v: any) => setSessions(sessions.map((s) => (s.key === key ? { ...s, f: { ...s.f, [k]: v } } : s)));

  const changes = useMemo(() => {
    const program: Rec = {};
    PROGRAM_KEYS.forEach((k) => {
      if (!same(out(k, p[k]), orig[k])) program[k] = out(k, p[k]);
    });
    const ss: Rec[] = [];
    const order: string[] = [];
    sessions.forEach((s) => {
      if (s.removed) {
        if (s.id) {
          ss.push({ id: s.id, remove: true });
          order.push(s.key);
        }
        return;
      }
      const before = s.id ? origSessions.get(s.id) ?? {} : {};
      const fields: Rec = {};
      SESSION_KEYS.forEach((k) => {
        if (!same(out(k, s.f[k]), s.id ? before[k] : null)) fields[k] = out(k, s.f[k]);
      });
      if (Object.keys(fields).length || !s.id) {
        ss.push(s.id ? { id: s.id, fields } : { fields });
        order.push(s.key);
      }
    });
    const body: Rec = { program, sessions: ss };
    if (!same([...topics].sort(), [...origTopics].sort())) body.topics = topics;
    if (comp.label !== origComp || comp.rate) {
      body.competitiveness = { label: comp.label, acceptance_rate: comp.rate ? Number(comp.rate) / 100 : null, source: comp.source || null };
    }
    if (isNew) body.program_name = name.trim();
    return { body, order };
  }, [p, orig, sessions, origSessions, topics, origTopics, comp, origComp, name, isNew]);

  const { body: payload, order: sessionOrder } = changes;
  const changed = isNew || Object.keys(payload.program).length > 0 || payload.sessions.length > 0 || 'topics' in payload || 'competitiveness' in payload;

  const send = async () => {
    setBusy(true);
    setErrors([]);
    try {
      const r = isNew ? await partnerApi.create(payload) : await partnerApi.save(pid, payload);
      toast.success(r.message);
      onSaved();
      history.push('/partners');
    } catch (e: any) {
      const errs = e?.response?.data?.errors;
      if (Array.isArray(errs)) {
        setErrors(errs);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else showError(e);
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && data.error) return <p className="body" style={{ marginTop: 24 }}>We could not open this program. It may belong to another organization.</p>;
  if (!isNew && !data.data) return <p className="muted" style={{ marginTop: 24 }}>Loading</p>;

  const live = sessions.filter((s) => !s.removed);
  const prices = live.flatMap((s) => (s.f.is_free ? [0] : [s.f.cost_min, s.f.cost_max])).filter((v) => v !== null && v !== undefined && v !== '').map(Number);
  const allTopics = (topicGroups.data ?? []).flatMap((g) => g.topics);
  const title = isNew ? name || 'New program' : p.display_name || data.data?.program.program_name;
  const fieldErr = (k: string) => errFor(`program.${k}`);

  const Text = ({ k, label, ph, area }: { k: string; label: string; ph?: string; area?: boolean }) => (
    <div>
      <label className="lbl" htmlFor={`pe-${k}`}>{label}</label>
      {area ? (
        <textarea id={`pe-${k}`} className="in" placeholder={ph} value={p[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
      ) : (
        <input id={`pe-${k}`} className="in" placeholder={ph} value={p[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
      )}
      {fieldErr(k) && <p className="ep-err">{fieldErr(k)}</p>}
    </div>
  );
  const Num = ({ k, label }: { k: string; label: string }) => (
    <div>
      <label className="lbl" htmlFor={`pe-${k}`}>{label}</label>
      <input id={`pe-${k}`} className="in" inputMode="decimal" value={p[k] ?? ''} onChange={(e) => set(k, e.target.value.replace(/[^\d.]/g, ''))} />
      {fieldErr(k) && <p className="ep-err">{fieldErr(k)}</p>}
    </div>
  );
  const Check = ({ k, label }: { k: string; label: string }) => (
    <label className="check"><input type="checkbox" checked={!!p[k]} onChange={(e) => set(k, e.target.checked)} />{label}</label>
  );
  const Sel = ({ k, label, opts }: { k: string; label: string; opts: [string, string][] }) => (
    <div>
      <label className="lbl" htmlFor={`pe-${k}`}>{label}</label>
      <select id={`pe-${k}`} className="in" value={p[k] ?? ''} onChange={(e) => set(k, e.target.value || null)}>
        <option value="">Not set</option>
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  return (
    <>
      <button type="button" className="btn sm" style={{ marginTop: 16 }} onClick={() => history.push('/partners')}>← Your programs</button>
      <div className="between" style={{ margin: '12px 0 16px', flexWrap: 'wrap' }}>
        <div>
          <h1>{title}</h1>
          <p className="muted small" style={{ marginTop: 4 }}>
            {isNew ? 'A new program' : `GradMap #${pid}`}
            {data.data?.pending_update?.status === 'pending' ? ' · An update is waiting for GradMap' : ''}
          </p>
        </div>
        <button type="button" className="btn primary" disabled={busy || !changed} onClick={send}>
          {me.publishes_directly ? 'Publish changes' : 'Send to GradMap'}
        </button>
      </div>

      {needs && (
        <div className="panel" style={{ background: 'var(--gm-warning-wash, #fdf3e2)', color: 'var(--gm-warning-ink, #b45309)', marginBottom: 12 }}>
          {`From GradMap: ${needs.message ? `"${needs.message}"` : 'please check this program.'}`}
          {needs.fields.length ? ` Fields to fix: ${needs.fields.join(', ')}.` : ''}
        </div>
      )}
      {errors.length > 0 && (
        <div className="panel" role="alert" style={{ background: 'var(--gm-danger-wash, #fdeded)', color: 'var(--gm-danger-ink, #b02525)', marginBottom: 12 }}>
          <b style={{ fontWeight: 500 }}>Some fields need fixing:</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {errors.map((e) => <li key={e.field}>{`${e.field.replace(/^program\./, '').replace(/_/g, ' ')} ${e.message}`}</li>)}
          </ul>
        </div>
      )}

      <div className="ep-editor">
        <nav className="ep-enav" aria-label="Sections">
          {SECTIONS.map(([k, l]) => <a key={k} href={`#pe-sec-${k}`} onClick={(e) => { e.preventDefault(); document.getElementById(`pe-sec-${k}`)?.scrollIntoView({ behavior: 'smooth' }); }}>{l}</a>)}
        </nav>

        <div>
          <section className="card" id="pe-sec-basics">
            <h2>Basics</h2>
            <div className="ep-grid2" style={{ marginTop: 12 }}>
              {isNew ? (
                <div>
                  <label className="lbl" htmlFor="pe-name">Program name</label>
                  <input id="pe-name" className="in" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
                  {errFor('program_name') && <p className="ep-err">{errFor('program_name')}</p>}
                </div>
              ) : (
                Text({ k: 'display_name', label: 'Name families see' })
              )}
              {Sel({ k: 'program_type', label: 'Type of program', opts: Object.entries(TYPE_LABEL) })}
              {Sel({ k: 'when_offered', label: 'When it runs', opts: [['summer', 'Summer'], ['year_round', 'Year-round']] })}
              {Sel({ k: 'program_status', label: 'This year', opts: [['running', 'Running'], ['not_running_this_year', 'Not running this year'], ['ended', 'Ended for good']] })}
            </div>
            <div className="stack" style={{ marginTop: 12 }}>
              {Text({ k: 'notes', label: 'Description', area: true, ph: 'What students do, who teaches, what they leave with' })}
              {Text({ k: 'website', label: 'Program web page', ph: 'https://' })}
            </div>
          </section>

          <section className="card" id="pe-sec-sessions">
            <div className="between" style={{ alignItems: 'center' }}>
              <h2>Sessions</h2>
              <button type="button" className="btn sm" onClick={() => setSessions([...sessions, { key: newKey(), f: { delivery_mode: 'in_person', is_host_location: true } }])}>+ Add a session</button>
            </div>
            <p className="muted small" style={{ marginTop: 4 }}>One session for each set of dates, place or price. Students pick a session when they apply, and their checklist counts back from its deadline.</p>
            {errFor('sessions') && <p className="ep-err">{errFor('sessions')}</p>}
            {sessions.map((s, i) => {
              if (s.removed) return null;
              const se = (k: string) => errFor(`sessions[${sessionOrder.indexOf(s.key)}].${k}`);
              const In = (k: string, label: string, type = 'text') => (
                <div>
                  <label className="lbl" htmlFor={`ps-${s.key}-${k}`}>{label}</label>
                  <input id={`ps-${s.key}-${k}`} className="in" type={type} inputMode={NUM.has(k) ? 'decimal' : undefined} value={s.f[k] ?? ''} onChange={(e) => setS(s.key, k, NUM.has(k) ? e.target.value.replace(/[^\d.]/g, '') : e.target.value)} />
                  {se(k) && <p className="ep-err">{se(k)}</p>}
                </div>
              );
              return (
                <div key={s.key} className="ep-session">
                  <div className="between" style={{ alignItems: 'center' }}>
                    <h3>{s.f.label || `Session ${i + 1}`}</h3>
                    <div className="row">
                      <button type="button" className="btn sm" onClick={() => setSessions([...sessions, { key: newKey(), f: { ...s.f, label: s.f.label ? `${s.f.label} (copy)` : null } }])}>Copy</button>
                      <button type="button" className="btn sm" style={{ color: 'var(--gm-danger-ink, #b02525)' }} disabled={live.length <= 1} onClick={() => setSessions(sessions.map((x) => (x.key === s.key ? { ...x, removed: true } : x)))}>Remove</button>
                    </div>
                  </div>
                  <div className="ep-grid3" style={{ marginTop: 10 }}>
                    {In('label', 'Name (optional)')}
                    {In('start_date', 'Starts', 'date')}
                    {In('end_date', 'Ends', 'date')}
                    <div>
                      <label className="lbl" htmlFor={`ps-${s.key}-dm`}>Format</label>
                      <select id={`ps-${s.key}-dm`} className="in" value={s.f.delivery_mode ?? ''} onChange={(e) => setS(s.key, 'delivery_mode', e.target.value || null)}>
                        <option value="in_person">In person</option><option value="online">Online</option><option value="hybrid">Hybrid</option><option value="online_or_in_person">Online or in person</option>
                      </select>
                    </div>
                    <div>
                      <label className="lbl" htmlFor={`ps-${s.key}-ht`}>Housing</label>
                      <select id={`ps-${s.key}-ht`} className="in" value={s.f.housing_type ?? ''} onChange={(e) => setS(s.key, 'housing_type', e.target.value || null)}>
                        <option value="">Not set</option><option value="residential">Residential</option><option value="commuter">Commuter</option><option value="both">Residential or commuter</option><option value="not_applicable">Not applicable</option>
                      </select>
                    </div>
                    {In('hours_per_week', 'Hours per week (optional)')}
                    {s.f.delivery_mode !== 'online' && (
                      <>
                        {In('location_name', 'Held at')}
                        {In('city', 'City')}
                        {In('zip', 'Zip code')}
                      </>
                    )}
                  </div>
                  {s.f.delivery_mode !== 'online' && (
                    <>
                      <label className="check" style={{ marginTop: 10 }}><input type="checkbox" checked={s.f.is_host_location !== false} onChange={(e) => setS(s.key, 'is_host_location', e.target.checked)} />We own or run this location</label>
                      <p className="muted small" style={{ marginTop: 4 }}>The zip puts this session on the map for students searching near them.</p>
                    </>
                  )}
                  <div className="ep-grid3" style={{ marginTop: 10 }}>
                    {In('application_open_date', 'Applications open', 'date')}
                    {In('priority_deadline', 'Priority deadline (optional)', 'date')}
                    {In('deadline_date', 'Regular deadline', 'date')}
                    {In('decision_date', 'Decisions sent by', 'date')}
                    {!s.f.is_free && In('cost_min', 'Price from')}
                    {!s.f.is_free && In('cost_max', 'Price up to')}
                  </div>
                  <label className="check" style={{ marginTop: 10 }}><input type="checkbox" checked={!!s.f.is_free} onChange={(e) => setS(s.key, 'is_free', e.target.checked)} />Free to attend</label>
                  <div style={{ marginTop: 10 }}>{In('price_includes', 'What the price includes (optional)')}</div>
                </div>
              );
            })}
          </section>

          <section className="card" id="pe-sec-who">
            <h2>Who can apply</h2>
            <span className="lbl" style={{ marginTop: 12 }}>Grades</span>
            <div className="row" role="group" aria-label="Grades">
              {[6, 7, 8, 9, 10, 11, 12].map((g) => {
                const on = (p.grades_eligible ?? []).includes(g);
                return <button key={g} type="button" className={`chipb ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => set('grades_eligible', on ? (p.grades_eligible ?? []).filter((x: number) => x !== g) : [...(p.grades_eligible ?? []), g].sort((a, b) => a - b))}>{g}</button>;
              })}
            </div>
            {fieldErr('grades_eligible') && <p className="ep-err">{fieldErr('grades_eligible')}</p>}
            <div className="ep-grid3" style={{ marginTop: 12 }}>
              {Num({ k: 'age_min', label: 'Youngest age (optional)' })}
              {Num({ k: 'age_max', label: 'Oldest age (optional)' })}
              {Num({ k: 'gpa_min', label: 'Minimum GPA (optional)' })}
              {Sel({ k: 'citizenship_requirement', label: 'Citizenship', opts: [['none', 'Open to everyone'], ['us_citizen', 'US citizens'], ['us_citizen_or_pr', 'US citizens and permanent residents'], ['other', 'Other (explain below)']] })}
              {Sel({ k: 'gender_restriction', label: 'Open to', opts: [['none', 'All students'], ['girls_women', 'Girls and young women'], ['other', 'Other (explain below)']] })}
              <div>
                <label className="lbl" htmlFor="pe-res">State residents only (optional)</label>
                <input id="pe-res" className="in" placeholder="e.g. DC, MD, VA" value={(p.residency_states ?? []).join(', ')} onChange={(e) => set('residency_states', e.target.value.split(/[\s,]+/).map((x) => x.toUpperCase()).filter(Boolean))} />
                {fieldErr('residency_states') && <p className="ep-err">{fieldErr('residency_states')}</p>}
              </div>
            </div>
            <div className="stack" style={{ marginTop: 12 }}>
              {Check({ k: 'first_gen_or_income_based', label: 'For first-generation or lower-income students' })}
              {Text({ k: 'prior_experience_note', label: 'Experience needed (optional)', ph: 'e.g. one year of biology' })}
            </div>
          </section>

          <section className="card" id="pe-sec-needs">
            <h2>What students need to apply</h2>
            <p className="muted small" style={{ marginTop: 4 }}>This builds each student's checklist, with target dates counted back from your deadline.</p>
            <div className="ep-grid3" style={{ marginTop: 12 }}>
              {Num({ k: 'essays_count', label: 'Essays' })}
              {Num({ k: 'essays_word_count', label: 'Word limit per essay' })}
              {Num({ k: 'teacher_recommendations_count', label: 'Teacher letters' })}
              {Num({ k: 'general_recommendations_count', label: 'Other letters (coach, mentor)' })}
              {Text({ k: 'application_fee', label: 'Application fee', ph: 'e.g. $75' })}
            </div>
            <div className="stack" style={{ marginTop: 12 }}>
              {Text({ k: 'essays_topics', label: 'Essay prompts', area: true, ph: '1. Why do you want to study medicine? 2. ...' })}
              <div className="row" style={{ gap: 16 }}>
                {Check({ k: 'fee_waiver_available', label: 'Families can ask for a fee waiver' })}
                {Check({ k: 'transcript_required', label: 'Transcript' })}
                {Check({ k: 'test_scores', label: 'Test scores' })}
                {Check({ k: 'resume', label: 'Resume' })}
                {Check({ k: 'interview_required', label: 'Interview' })}
                {Check({ k: 'portfolio_required', label: 'Portfolio' })}
                {Check({ k: 'audition_required', label: 'Audition' })}
              </div>
            </div>
          </section>

          <section className="card" id="pe-sec-money">
            <h2>Money</h2>
            <div className="row" style={{ gap: 16, marginTop: 12 }}>
              {Check({ k: 'financial_aid_available', label: 'Financial aid available' })}
              {Check({ k: 'need_based_aid', label: 'Need-based financial aid' })}
              {Check({ k: 'merit_scholarship', label: 'Merit scholarships' })}
              {Check({ k: 'full_scholarship_available', label: 'Full scholarships available' })}
              {Check({ k: 'pays_students', label: 'Pays students (stipend or wage)' })}
            </div>
            <p className="muted small" style={{ marginTop: 8 }}>Prices are set on each session above.</p>
          </section>

          <section className="card" id="pe-sec-topics">
            <div className="between" style={{ alignItems: 'center' }}>
              <h2>Topics</h2>
              <span className="chip a">{`${topics.length} of 5 picked`}</span>
            </div>
            <p className="muted small" style={{ marginTop: 4 }}>Families find programs by topic. Pick up to 5 from GradMap's list.</p>
            {errFor('topics') && <p className="ep-err">{errFor('topics')}</p>}
            {(topicGroups.data ?? []).map((g) => (
              <div key={g.key} style={{ marginTop: 12 }}>
                <span className="lbl">{g.label}</span>
                <div className="row">
                  {g.topics.map((t) => {
                    const on = topics.includes(t.slug);
                    return (
                      <button key={t.slug} type="button" className={`chipb ${on ? 'on' : ''}`} aria-pressed={on} disabled={!on && topics.length >= 5} onClick={() => setTopics(on ? topics.filter((x) => x !== t.slug) : [...topics, t.slug])}>
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="row" style={{ marginTop: 14, flexWrap: 'nowrap' }}>
              <input className="in" placeholder="Suggest a topic that is not here" aria-label="Suggest a topic" value={topicAsk} maxLength={80} onChange={(e) => setTopicAsk(e.target.value)} />
              <button
                type="button"
                className="btn"
                disabled={topicAsk.trim().length < 3}
                onClick={async () => {
                  try {
                    await partnerApi.requestTopic(topicAsk.trim(), isNew ? null : pid);
                    setTopicAsk('');
                    toast.success('Thanks. GradMap reviews new topics each month.');
                  } catch (e) {
                    showError(e);
                  }
                }}
              >
                Suggest
              </button>
            </div>
            {allTopics.length === 0 && <p className="muted small">Loading topics</p>}
          </section>

          <section className="card" id="pe-sec-competitive">
            <h2>How competitive</h2>
            <p className="muted small" style={{ marginTop: 4 }}>This describes your program, never a student's chances. GradMap never shows odds. Highly selective and Selective need a published acceptance rate and where it is posted.</p>
            <div className="row" style={{ marginTop: 12 }} role="group" aria-label="How competitive">
              {Object.entries(COMPETITIVENESS).map(([k, v]) => (
                <button key={k} type="button" className={`chipb ${comp.label === k ? 'on' : ''}`} aria-pressed={comp.label === k} onClick={() => setComp({ ...comp, label: k })}>{v.label}</button>
              ))}
              <button type="button" className={`chipb ${comp.label === null ? 'on' : ''}`} aria-pressed={comp.label === null} onClick={() => setComp({ label: null, rate: '', source: '' })}>Let GradMap decide</button>
            </div>
            {comp.label && <p className="body small" style={{ marginTop: 8 }}>{COMPETITIVENESS[comp.label].help}.</p>}
            {(comp.label === 'highly_selective' || comp.label === 'selective') && (
              <div className="ep-grid2" style={{ marginTop: 12 }}>
                <div>
                  <label className="lbl" htmlFor="pe-rate">Published acceptance rate (%)</label>
                  <input id="pe-rate" className="in" inputMode="decimal" value={comp.rate} onChange={(e) => setComp({ ...comp, rate: e.target.value.replace(/[^\d.]/g, '') })} />
                  {errFor('competitiveness.acceptance_rate') && <p className="ep-err">{errFor('competitiveness.acceptance_rate')}</p>}
                </div>
                <div>
                  <label className="lbl" htmlFor="pe-src">Where it is posted</label>
                  <input id="pe-src" className="in" placeholder="https://" value={comp.source} onChange={(e) => setComp({ ...comp, source: e.target.value })} />
                  {errFor('competitiveness.source') && <p className="ep-err">{errFor('competitiveness.source')}</p>}
                </div>
              </div>
            )}
          </section>

          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn" onClick={() => history.push('/partners')}>Cancel</button>
            <button type="button" className="btn primary" disabled={busy || !changed} onClick={send}>{me.publishes_directly ? 'Publish changes' : 'Send to GradMap'}</button>
          </div>
        </div>

        <aside className="ep-eside">
          <section className="card">
            <span className="lbl">What a student sees</span>
            <h3>{title}</h3>
            <p className="muted small" style={{ fontWeight: 500 }}>{me.org.name}</p>
            <div className="row" style={{ marginTop: 8 }}>
              {topics.slice(0, 3).map((t) => <span key={t} className="chip">{allTopics.find((x) => x.slug === t)?.name ?? t}</span>)}
              {comp.label && <span className="chip">{COMPETITIVENESS[comp.label].label}</span>}
            </div>
            <p className="body small" style={{ marginTop: 8 }}>
              {[p.program_type ? TYPE_LABEL[p.program_type] : '', deliveryText(live.map((s) => s.f.delivery_mode)), `${live.length} ${live.length === 1 ? 'session' : 'sessions'}`].filter(Boolean).join(' · ')}
            </p>
            <p className="small" style={{ marginTop: 4, fontWeight: 500 }}>
              {live.every((s) => s.f.is_free) ? 'Free' : prices.length ? (Math.min(...prices) === Math.max(...prices) ? money(Math.min(...prices)) : `${money(Math.min(...prices))} to ${money(Math.max(...prices))}`) : UNKNOWN_COST}
            </p>
          </section>
          <section className="card">
            <h3>Before you send</h3>
            <p className="body small" style={{ marginTop: 6, lineHeight: 1.55 }}>
              {me.publishes_directly
                ? 'Your changes publish right away. Students who follow or track this program get a notice when dates change.'
                : `GradMap checks this update, usually within 2 business days. ${me.reviewed_updates_left} reviewed ${me.reviewed_updates_left === 1 ? 'update' : 'updates'} left. Students who follow this program get a notice when dates change.`}
            </p>
          </section>
        </aside>
      </div>
    </>
  );
}
